import { Box, Button, Chip, Drawer, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { CheckCircleOutline, OpenInNewOutlined, ReplayOutlined } from '@mui/icons-material'
import { FunctionComponent, useContext, useMemo, useState } from 'react'
import { TableDisplay } from '../../miscellaneous-components/table-display'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import { IBillDocument } from '../../../../shared/services/file-upload/file-upload.model'
import FileUploadService from '../../../../shared/services/file-upload/file-upload.service'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { segmentedToggleSx } from '../../../../shared/utils/segmented-toggle-sx'
import { openBillDocument } from '../../../../shared/utils/open-bill-document'
import {
  BILL_OUTCOME_COLOURS,
  BILL_OUTCOME_LABELS,
  BillOutcome,
  billDocumentPeriod,
  billDocumentRowsText,
  billNeedsALook,
  billOutcome,
  isBillReading,
} from '../../../../shared/utils/bill-upload-utils'
import { BillDocumentDetail } from './bill-document-detail'

/**
 * BillDocumentsList
 * -------------------------------------------------------------------------
 * One row per bill in a bill upload, with what became of it (rows, credit note, held back by a
 * credit note, could not be read). Clicking a bill opens a drawer with what was read off it, the
 * document itself, and the reviewer's actions: read it again, overrule a credit-note call, or say a
 * bill is not the one a credit note cancels.
 *
 * Used on the import review step and on the upload's Bills tab in import history.
 */

export type BillListFilter = 'all' | 'attention' | 'credit'

// Bills that need a look come first, then everything else in the order they were uploaded.
const OUTCOME_RANK: Record<BillOutcome, number> = {
  'cancelled': 0,
  'unreadable': 1,
  'needs-attention': 2,
  'reading': 3,
  'credit-note': 4,
  'ready': 5,
  'no-rows': 6,
  'discarded': 7,
  'imported': 8,
}

const noteFor = (document: IBillDocument): string => {
  if (document.cancelledBy) return `Cancelled by ${document.cancelledBy.filename}`
  if (document.duplicateOfBillDocumentId) return `Possible duplicate of ${document.duplicateOf?.filename || 'a bill already imported'}`
  const text = document.errorMessage || ''
  return text.length > 90 ? `${text.substring(0, 90)}...` : text
}

export const BillDocumentsList: FunctionComponent<{
  documents: IBillDocument[]
  fileUploadId: string
  /** Called after any action changes the bills or their rows, so the caller can refetch both. */
  onChanged: () => void
  filter: BillListFilter
  onFilterChange: (filter: BillListFilter) => void
  /** A locked period, or a reviewer without rights: the list and drawer, without the actions. */
  readOnly?: boolean
}> = ({ documents, fileUploadId, onChanged, filter, onFilterChange, readOnly = false }) => {
  const fileUploadService = new FileUploadService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  const [openDocumentId, setOpenDocumentId] = useState<string | null>(null)
  const [perPage, setPerPage] = useState(10)
  const [isActing, setIsActing] = useState(false)

  const needingALook = documents.filter(billNeedsALook)
  const creditNotes = documents.filter((document) => billOutcome(document) === 'credit-note')
  const shown = filter === 'attention' ? needingALook : filter === 'credit' ? creditNotes : documents
  const openDocument = documents.find((document) => document.id === openDocumentId) || null

  const headCells: IHeadCell[] = [
    { id: 'bill', numeric: false, label: 'Bill', sortable: false },
    { id: 'status', numeric: false, label: 'Status', sortable: false },
    { id: 'rows', numeric: false, label: 'Rows', sortable: false },
    { id: 'supplier', numeric: false, label: 'Supplier', sortable: false },
    { id: 'meter', numeric: false, label: 'Meter', sortable: false },
    { id: 'period', numeric: false, label: 'Period', sortable: false },
    { id: 'note', numeric: false, label: 'Note', sortable: false },
  ]

  const rows = useMemo(() => shown
    .map((document, index) => {
      const outcome = billOutcome(document)
      return {
        id: document.id,
        sortRank: OUTCOME_RANK[outcome] * 10000 + index,
        bill: (
          <Typography variant='body2' sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={document.filename}>
            {document.filename}
          </Typography>
        ),
        status: (
          <Stack spacing={0.5} alignItems='flex-start'>
            <Chip size='small' variant='outlined' color={BILL_OUTCOME_COLOURS[outcome]} label={BILL_OUTCOME_LABELS[outcome]} />
            {!!document.duplicateOfBillDocumentId && (outcome === 'ready' || outcome === 'needs-attention') && (
              <Chip size='small' variant='outlined' color='info' label='Possible duplicate' />
            )}
          </Stack>
        ),
        rows: billDocumentRowsText(document),
        supplier: document.extractedFields?.SUPPLIER?.value ? String(document.extractedFields.SUPPLIER.value) : '',
        meter: document.meterReference || '',
        period: billDocumentPeriod(document),
        note: noteFor(document),
      }
    }), [shown])

  const confirm = (title: string, message: string, buttonCopy: string) =>
    userNotificationsContext.openDialog({ active: true, title, message, buttonCopy, cancelButtonCopy: 'Cancel' })

  const runAction = async (action: () => Promise<any>, successMessage: string) => {
    if (isActing) return
    setIsActing(true)
    try {
      await action()
      userNotificationsContext.openSnackbar({ open: true, message: successMessage })
      onChanged()
    } catch (error: any) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: error?.response?.data?.message || 'There was an error, please try again',
        backgroundColor: 'text.primary',
      })
    } finally {
      setIsActing(false)
    }
  }

  const readAgainHandler = async (document: IBillDocument) => {
    const hasRows = (document.outstandingRowCount || 0) > 0
    const confirmed = await confirm(
      'Read this bill again',
      hasRows
        ? "We'll read this bill again and replace its rows that aren't imported yet with what we find. Any changes you've made to those rows will be lost."
        : "We'll read this bill again and add rows for anything we find.",
      'Read again',
    )
    if (!confirmed) return
    await runAction(() => fileUploadService.updateBillDocumentType(document.id, document.billType || 'energy'), 'Reading this bill again')
  }

  const notACreditNoteHandler = async (document: IBillDocument) => {
    const confirmed = await confirm(
      'Not a credit note',
      'This bill will get a blank row for you to fill in from the document. Anything it was holding back in this upload is released.',
      'Add a row',
    )
    if (!confirmed) return
    await runAction(() => fileUploadService.addBillDocumentManualRow(document.id), 'Added a row for this bill. Fill in the usage from the bill.')
  }

  const notCancelledHandler = async (document: IBillDocument) => {
    const confirmed = await confirm(
      'Not cancelled by this credit note',
      `${document.filename} will no longer be held back by ${document.cancelledBy?.filename || 'the credit note'}, and its rows can import. Only do this if the credit note cancels a different bill.`,
      'Release',
    )
    if (!confirmed) return
    await runAction(() => fileUploadService.dismissBillDocumentCancellation(document.id), 'This bill is no longer held back')
  }

  const confirmRenewableHandler = async (document: IBillDocument) => {
    const confirmed = await confirm(
      'Confirm renewable electricity',
      `The market-based emissions for ${document.filename} will be recorded as 0 kgCO2e. Only confirm if its renewable tariff covers these months.`,
      'Confirm',
    )
    if (!confirmed) return
    await runAction(() => fileUploadService.decideBillDocumentRenewable(document.id, 'confirm'), 'Confirmed as renewable')
  }

  const notRenewableHandler = (document: IBillDocument) =>
    runAction(() => fileUploadService.decideBillDocumentRenewable(document.id, 'dismiss'), 'Marked as not renewable')

  const viewBillHandler = async (document: IBillDocument) => {
    const opened = await openBillDocument(document.id)
    if (!opened) {
      userNotificationsContext.openSnackbar({ open: true, message: 'Could not open this bill. Please try again.', backgroundColor: 'text.primary' })
    }
  }

  const openOutcome = openDocument ? billOutcome(openDocument) : null
  const canReadAgain = !!openDocument && !readOnly && !isBillReading(openDocument) && (openDocument.committedRowCount || 0) === 0
  const canAddRow = !!openDocument && !readOnly && openOutcome === 'credit-note'
  const canRelease = !!openDocument && !readOnly && openOutcome === 'cancelled'
  // Renewable evidence is decided while the bill still has rows waiting; an imported figure is changed on the emission.
  const canDecideRenewable = !!openDocument && !readOnly && !!openDocument.renewableEvidence && (openDocument.outstandingRowCount || 0) > 0
  const renewableFullyCovered = typeof openDocument?.renewableEvidence?.coveragePercent === 'number' && openDocument.renewableEvidence.coveragePercent >= 99.95

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_event, next) => { if (next !== null) onFilterChange(next) }}
          size='small'
          aria-label='Which bills'
          sx={segmentedToggleSx}
        >
          <ToggleButton value='all' aria-label='All bills'>All bills ({documents.length})</ToggleButton>
          <ToggleButton value='attention' aria-label='Bills that need a look'>Needs a look ({needingALook.length})</ToggleButton>
          {creditNotes.length > 0 && <ToggleButton value='credit' aria-label='Credit notes'>Credit notes ({creditNotes.length})</ToggleButton>}
        </ToggleButtonGroup>
      </Box>
      {rows.length > 0 ? (
        <TableDisplay
          headCells={headCells}
          rows={rows}
          selected={[]}
          setSelected={() => { }}
          perPage={perPage}
          setPerPage={setPerPage}
          defaultOrderBy='sortRank'
          defaultOrder='asc'
          onRowClick={(documentId: string) => setOpenDocumentId(documentId)}
        />
      ) : (
        <Typography variant='body2' sx={{ color: 'text.secondary', py: 2 }}>
          {filter === 'attention' ? 'Nothing here needs a look.' : 'No bills to show.'}
        </Typography>
      )}

      <Drawer
        anchor='right'
        open={!!openDocument}
        onClose={() => setOpenDocumentId(null)}
        PaperProps={{
          sx: {
            top: { xs: 0, sm: 64 },
            height: { xs: '100%', sm: 'calc(100vh - 64px)' },
            borderTopLeftRadius: { xs: 0, sm: 24 },
            borderBottomLeftRadius: { xs: 0, sm: 24 },
            overflow: 'hidden',
            boxShadow: '-12px 0 32px rgba(17, 30, 33, 0.14)',
            borderLeft: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        {openDocument && openOutcome && (
          <Box sx={{ width: { xs: '100vw', sm: 480 }, maxWidth: '100vw', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
            <Stack direction='row' justifyContent='space-between' alignItems='flex-start' sx={{ px: 3, py: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant='caption' color='text.secondary'>Bill</Typography>
                <Typography variant='h6' sx={{ color: 'text.primary', lineHeight: 1.2, wordBreak: 'break-word' }}>
                  {openDocument.filename}
                </Typography>
                <Stack direction='row' spacing={1} sx={{ mt: 1 }}>
                  <Chip size='small' variant='outlined' color={BILL_OUTCOME_COLOURS[openOutcome]} label={BILL_OUTCOME_LABELS[openOutcome]} />
                  <Typography variant='caption' color='text.secondary' sx={{ alignSelf: 'center' }}>
                    Rows: {billDocumentRowsText(openDocument)}
                    {(openDocument.rowNumbers || []).length > 0 ? ` (row ${(openDocument.rowNumbers || []).join(', ')})` : ''}
                  </Typography>
                </Stack>
              </Box>
              <IconButton aria-label='Close' onClick={() => setOpenDocumentId(null)}><CloseIcon /></IconButton>
            </Stack>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 3 }}>
              <BillDocumentDetail document={openDocument} fileUploadId={fileUploadId} />
            </Box>

            <Stack direction='row' justifyContent='flex-end' alignItems='center' flexWrap='wrap' gap={1} sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
              {canRelease && (
                <Button variant='outlined' disabled={isActing} onClick={() => notCancelledHandler(openDocument)}>
                  <Typography>Not cancelled</Typography>
                </Button>
              )}
              {canDecideRenewable && openDocument.renewableDecision !== 'dismissed' && (
                <Button variant='outlined' disabled={isActing} onClick={() => notRenewableHandler(openDocument)}>
                  <Typography>Not renewable</Typography>
                </Button>
              )}
              {canDecideRenewable && renewableFullyCovered && openDocument.renewableDecision !== 'confirmed' && (
                <Button variant='outlined' disabled={isActing} startIcon={<CheckCircleOutline fontSize='small' />} onClick={() => confirmRenewableHandler(openDocument)}>
                  <Typography sx={{ pl: 0.5 }}>Confirm renewable</Typography>
                </Button>
              )}
              {canAddRow && (
                <Button variant='outlined' disabled={isActing} onClick={() => notACreditNoteHandler(openDocument)}>
                  <Typography>Not a credit note</Typography>
                </Button>
              )}
              {canReadAgain && (
                <Button variant='outlined' disabled={isActing} startIcon={<ReplayOutlined fontSize='small' />} onClick={() => readAgainHandler(openDocument)}>
                  <Typography sx={{ pl: 0.5 }}>Read again</Typography>
                </Button>
              )}
              <Button variant='contained' startIcon={<OpenInNewOutlined fontSize='small' />} onClick={() => viewBillHandler(openDocument)}>
                <Typography sx={{ pl: 0.5 }}>View bill</Typography>
              </Button>
            </Stack>
          </Box>
        )}
      </Drawer>
    </Box>
  )
}
